import { Alert, Chip, Tooltip } from "@heroui/react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Circle, CircleHelp, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ruleHref } from "../playbook/ruleAnchors";
import {
  STAGE_META, archetypeLabel, fmt, pct, useClosePreview, useCountdown,
  type ClosePreview, type Plan, type Stage, type Status, type Verdict,
} from "./deskData";

// Presentational pieces shared by the Simple and Pro desks.

export function StageChip({ stage }: { stage: Stage }) {
  const m = STAGE_META[stage];
  return (
    <Tooltip delay={300}>
      <Tooltip.Trigger aria-label={`${m.label}: ${m.hint}`}>
        <Chip size="sm" variant="soft" color={m.color} className="font-semibold">{m.label}</Chip>
      </Tooltip.Trigger>
      <Tooltip.Content className="max-w-60 text-xs">{m.hint}</Tooltip.Content>
    </Tooltip>
  );
}

export function StatusIcon({ status, warn }: { status: Status; warn?: boolean }) {
  if (warn) return <AlertTriangle className="size-4 shrink-0 text-warning" aria-label="allowed, with a caution" />;
  if (status === "met") return <CheckCircle2 className="size-4 shrink-0 text-profit" aria-label="met" />;
  if (status === "failed") return <XCircle className="size-4 shrink-0 text-loss" aria-label="failed" />;
  return <Circle className="size-4 shrink-0 text-warning" aria-label="pending" />;
}

/** "?" after a checklist line: opens the Playbook card that explains that rule, with a picture and an example. */
export function RuleHelp({ ruleKey, label }: { ruleKey: string; label: string }) {
  const href = ruleHref(ruleKey);
  if (!href) return null;
  return (
    <Link
      to={href}
      title="What is this? See it explained with a picture and an example"
      aria-label={`What is “${label}”? Open its explanation in the Playbook`}
      className="ml-1 inline-flex size-4 items-center justify-center rounded-full align-[-2px] text-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-focus"
    >
      <CircleHelp className="size-3.5" aria-hidden />
    </Link>
  );
}

export function Mini({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "profit" | "warning" }) {
  const c = tone === "profit" ? "text-profit" : tone === "warning" ? "text-warning" : "text-foreground";
  return (
    <div className="rounded-lg border border-border bg-surface-secondary px-2.5 py-2">
      <div className="text-2xs text-muted">{label}</div>
      <div className={cn("num text-sm font-semibold", c)}>{value}</div>
      {sub && <div className="truncate text-2xs text-muted" title={sub}>{sub}</div>}
    </div>
  );
}

/** Vertical ladder: TP2 / TP1 / ENTRY / SL spaced proportionally to price. */
export function PriceLadder({ isLong, p }: { isLong: boolean; p: Plan }) {
  const levels = [
    { key: "TP2", price: p.tp2, pct: p.tp2Pct, cls: "text-profit", dim: "text-profit/70", bar: "bg-profit" },
    { key: "TP1", price: p.tp1, pct: p.tp1Pct, cls: "text-profit", dim: "text-profit/70", bar: "bg-profit" },
    { key: "Entry", price: p.entry, pct: 0, cls: "text-foreground", dim: "text-foreground/70", bar: "bg-foreground" },
    { key: "Stop", price: p.stopLoss, pct: p.stopPct, cls: "text-loss", dim: "text-loss/70", bar: "bg-loss" },
  ].sort((a, b) => b.price - a.price);
  const hi = levels[0]!.price, lo = levels[levels.length - 1]!.price;
  const span = hi - lo || 1;
  const H = 180;
  const y = (price: number) => 12 + ((hi - price) / span) * H;
  const eY = y(p.entry), sY = y(p.stopLoss), tY = y(p.tp1);
  return (
    <div className="relative" style={{ height: H + 24 }}>
      <div className="absolute bottom-3 left-[70px] top-3 w-px bg-border" />
      <div className="absolute left-[66px] w-[9px] rounded bg-profit-soft" style={{ top: Math.min(eY, tY), height: Math.abs(eY - tY) }} />
      <div className="absolute left-[66px] w-[9px] rounded bg-loss-soft" style={{ top: Math.min(eY, sY), height: Math.abs(eY - sY) }} />
      {levels.map(l => (
        <div key={l.key} className="absolute left-0 right-0 flex items-center gap-2" style={{ top: y(l.price) - 8 }}>
          <span className={cn("w-[60px] text-right text-2xs font-semibold", l.cls)}>{l.key}</span>
          <span className={cn("size-2.5 rounded-full ring-2 ring-surface", l.bar)} />
          <span className={cn("num text-xs font-semibold", l.cls)}>${fmt(l.price)}</span>
          {l.key !== "Entry" && <span className={cn("num text-2xs", l.dim)}>{pct(l.pct)}</span>}
        </div>
      ))}
      <div className="absolute bottom-0 left-[70px] text-3xs text-muted">{isLong ? "long: profit is up" : "short: profit is down"}</div>
    </div>
  );
}

/**
 * "If this hour closed now": the engine's answer on the candle still forming
 * (backend closePreview.ts), refreshed every 30 s. It answers the beginner's
 * question while waiting ("does this candle count yet?") without ever being
 * a signal: only the closed candle is.
 */
export function ClosePreviewBox({ preview: p, compact = false }: { preview: ClosePreview; compact?: boolean }) {
  const countdown = useCountdown(p.closesAt);
  const long = p.direction === "LONG";
  const closed = countdown.left <= 0;
  const close = fmt(p.candle.close);
  const pattern = long ? "a hammer or a bullish engulfing" : "a shooting star or a bearish engulfing";
  const triggerText = p.trigger
    ? `So far it counts as a trigger: ${p.trigger}${p.triggerLevel != null && /close/.test(p.trigger) ? ` (${close} is ${long ? "above" : "below"} ${fmt(p.triggerLevel)})` : ""}.`
    : p.triggerLevel != null
      ? `Not a trigger yet: it's at ${close}, and needs to close ${long ? "above" : "below"} ${fmt(p.triggerLevel)}, or form ${pattern}.`
      : `Not a trigger yet: it needs a long ${long ? "lower" : "upper"} wick at the range ${long ? "floor" : "ceiling"}.`;
  // The trigger row already says what's missing when that is the engine's reason too.
  const blocker = p.blocker && !p.blocker.startsWith("Not a trigger yet") ? p.blocker : null;
  const s = p.signal;
  return (
    <div className={cn("rounded-lg border border-border bg-surface-secondary leading-relaxed", compact ? "p-2 text-xs" : "p-3 text-sm")}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold">If this hour closed now</span>
        <span className="num shrink-0 text-xs text-muted">{closed ? "closed" : `closes in ${countdown.label}`}</span>
      </div>
      {closed ? (
        <p className="mt-1 text-muted">This candle just closed. The engine checks it about 30 seconds later; the desk updates right after.</p>
      ) : (
        <ul className="mt-1.5 space-y-1.5">
          <li className="flex gap-2">
            <StatusIcon status={p.trigger ? "met" : "pending"} />
            <span>{triggerText}</span>
          </li>
          <li className="flex gap-2">
            <StatusIcon status={s ? "met" : p.trigger ? "failed" : "pending"} />
            <span>
              {s
                ? <>The engine would send a <b className={s.action === "LONG" ? "text-profit" : "text-loss"}>{s.action === "LONG" ? "BUY" : "SELL"}</b>: entry ≈ <span className="num">{fmt(s.entry)}</span>, stop <span className="num">{fmt(s.stopLoss)}</span>, target 1 <span className="num">{fmt(s.tp1)}</span> ({s.riskReward.toFixed(1)}× the risk){s.againstBtc ? <>, at <span className="text-warning">half risk: against BTC</span></> : null}.</>
                : blocker
                  ? <>No signal at this close: {blocker}</>
                  : <>No signal until the trigger forms.</>}
            </span>
          </li>
        </ul>
      )}
      <p className={cn("mt-1.5 text-muted", compact ? "text-2xs" : "text-xs")}>
        A preview, not a signal: the candle can still change before it closes, and only the closed 1-hour candle counts.
        {!closed && p.volumePace < 0.5 && ` Volume so far is on pace for only ${p.volumePace.toFixed(1)}× a normal hour.`}
      </p>
    </div>
  );
}

/** The close preview for one Almost coin (nothing for other stages). */
export function ClosePreviewFor({ symbol, armed, compact }: { symbol: string; armed: boolean; compact?: boolean }) {
  const previews = useClosePreview(armed ? [symbol] : []);
  const p = previews[symbol];
  return armed && p ? <ClosePreviewBox preview={p} compact={compact} /> : null;
}

/**
 * A signal that goes against BTC's strong trend: the engine sends it at half
 * the usual risk instead of blocking it (backend evaluateSignal, backtest/btcgate).
 */
export function AgainstBtcChip({ action }: { action: "LONG" | "SHORT" }) {
  const hint = `BTC is trending hard ${action === "LONG" ? "down" : "up"}, and altcoins usually follow it. This ${action === "LONG" ? "buy" : "sell"} goes against that, so it trades at half your usual risk. Over 18 months, trades like this came out about even.`;
  return (
    <Tooltip delay={300}>
      <Tooltip.Trigger aria-label={`Against BTC, half risk: ${hint}`}>
        <Chip size="sm" variant="soft" color="warning" className="font-semibold">Against BTC · half risk</Chip>
      </Tooltip.Trigger>
      <Tooltip.Content className="max-w-64 text-xs">{hint}</Tooltip.Content>
    </Tooltip>
  );
}

/** "Can I trade this now?" in plain words, above the technical checklist. */
export function VerdictBox({ verdict: v }: { verdict: Verdict }) {
  const status = v.status === "enter" ? "success" : v.status === "almost" ? "warning" : v.status === "no-trade" && v.stage === "blocked" ? "danger" : "default";
  return (
    <Alert status={status}>
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{v.headline}</Alert.Title>
        <Alert.Description className="space-y-1">
          <span className="block">{v.explanation}</span>
          <span className="block font-medium">What to do: {v.next}</span>
        </Alert.Description>
      </Alert.Content>
    </Alert>
  );
}
